import React, { useState, useEffect } from 'react';
import { Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin, User, X, Edit, Trash2, Navigation, Mail, Phone, Bell, Camera, Star, Package } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import PhotoGalleryModal from './PhotoGalleryModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EVENT_TYPES = [
  { id: 'measurement', name: 'Measurement', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-100' },
  { id: 'installation', name: 'Installation', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-100' },
  { id: 'consultation', name: 'Consultation', color: 'bg-purple-500', textColor: 'text-purple-700', bgLight: 'bg-purple-100' },
  { id: 'revisit', name: 'Re-visit/Repair', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-100' },
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const emptyEvent = {
  title: '',
  event_type: 'installation',
  start_time: '',
  end_time: '',
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  installer_id: '',
  installer_name: '',
  notes: '',
  send_customer_email: false
};

export const ScheduleTab = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(emptyEvent);
  const [selectedDay, setSelectedDay] = useState(null);
  // Update confirmation modal state
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  const [updateNotifyOptions, setUpdateNotifyOptions] = useState({
    sendToAssignee: false,
    sendToCustomer: false
  });
  
  // Photo gallery modal state
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const [photoGalleryEvent, setPhotoGalleryEvent] = useState(null);
  
  // Job materials state
  const [showMaterialsModal, setShowMaterialsModal] = useState(false);
  const [materialsEvent, setMaterialsEvent] = useState(null);
  const [jobMaterials, setJobMaterials] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchInstallers();
    fetchProjects();
  }, [currentDate]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstallers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/installers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstallers(response.data);
    } catch (error) {
      console.error('Failed to fetch installers:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const saveEvent = async () => {
    if (!formData.title || !formData.start_time) {
      toast.error('Title and start time are required');
      return;
    }

    // If editing, show confirmation dialog to ask about notifications
    if (editingEvent) {
      setShowUpdateConfirm(true);
      return;
    }

    // New event - create directly
    try {
      await axios.post(`${API_URL}/admin/schedule`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event created');
      setShowEventModal(false);
      setEditingEvent(null);
      setFormData(emptyEvent);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  // Confirm update with notification options
  const confirmUpdate = async () => {
    try {
      const updateData = {
        ...formData,
        send_update_to_assignee: updateNotifyOptions.sendToAssignee,
        send_update_to_customer: updateNotifyOptions.sendToCustomer
      };
      
      await axios.put(`${API_URL}/admin/schedule/${editingEvent.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let successMsg = 'Event updated';
      if (updateNotifyOptions.sendToAssignee || updateNotifyOptions.sendToCustomer) {
        const notified = [];
        if (updateNotifyOptions.sendToAssignee) notified.push('assignee');
        if (updateNotifyOptions.sendToCustomer) notified.push('customer');
        successMsg += ` - Notifications sent to: ${notified.join(', ')}`;
      }
      toast.success(successMsg);
      
      setShowUpdateConfirm(false);
      setShowEventModal(false);
      setEditingEvent(null);
      setFormData(emptyEvent);
      setUpdateNotifyOptions({ sendToAssignee: false, sendToCustomer: false });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  // Send reminder for a specific event
  const sendReminder = async (eventId) => {
    try {
      const response = await axios.post(`${API_URL}/admin/schedule/${eventId}/send-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sentTo = response.data.sent_to || [];
      if (sentTo.length > 0) {
        const names = sentTo.map(s => s.name || s.email).join(', ');
        toast.success(`Reminder sent to: ${names}`);
      } else {
        toast.info('No recipients found for this event');
      }
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API_URL}/admin/schedule/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const openCreateModal = (date = null) => {
    setEditingEvent(null);
    const defaultDate = date || new Date();
    const dateStr = defaultDate.toISOString().split('T')[0];
    setFormData({
      ...emptyEvent,
      start_time: `${dateStr}T09:00`,
      end_time: `${dateStr}T11:00`
    });
    setShowEventModal(true);
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || '',
      event_type: event.event_type || 'installation',
      start_time: event.start_time?.slice(0, 16) || '',
      end_time: event.end_time?.slice(0, 16) || '',
      customer_name: event.customer_name || '',
      customer_email: event.customer_email || '',
      customer_phone: event.customer_phone || '',
      address: event.address || '',
      city: event.city || '',
      state: event.state || '',
      zip_code: event.zip_code || '',
      installer_id: event.installer_id || '',
      installer_name: event.installer_name || '',
      notes: event.notes || '',
      project_id: event.project_id || '',
      send_customer_email: event.send_customer_email || false
    });
    setShowEventModal(true);
  };

  // Helper to generate Google Maps directions URL
  const getGoogleMapsUrl = (address, city, state, zipCode) => {
    const fullAddress = [address, city, state, zipCode].filter(Boolean).join(', ');
    if (!fullAddress.trim()) return null;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
  };

  // Job Materials functions
  const openMaterialsModal = async (event) => {
    setMaterialsEvent(event);
    setLoadingMaterials(true);
    setShowMaterialsModal(true);
    
    try {
      // Fetch job materials
      const materialsRes = await axios.get(`${API_URL}/schedule/${event.id}/materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobMaterials(materialsRes.data);
      
      // Fetch inventory items for dropdown
      const inventoryRes = await axios.get(`${API_URL}/admin/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventoryItems(inventoryRes.data);
    } catch (error) {
      console.error('Failed to fetch materials:', error);
      toast.error('Failed to load materials');
    } finally {
      setLoadingMaterials(false);
    }
  };

  const addMaterial = async (inventoryItemId, quantity) => {
    try {
      await axios.post(`${API_URL}/schedule/${materialsEvent.id}/materials`, {
        inventory_item_id: inventoryItemId,
        quantity: quantity
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Material added');
      openMaterialsModal(materialsEvent); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add material');
    }
  };

  const removeMaterial = async (materialId) => {
    try {
      await axios.delete(`${API_URL}/schedule/${materialsEvent.id}/materials/${materialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Material removed');
      setJobMaterials(prev => prev.filter(m => m.id !== materialId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove material');
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, currentMonth: false, date: new Date(year, month - 1, prevMonthLastDay - i) });
    }
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, currentMonth: true, date: new Date(year, month, i) });
    }
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ day: i, currentMonth: false, date: new Date(year, month + 1, i) });
    }
    
    return days;
  };

  const getEventsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => e.start_time?.startsWith(dateStr));
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getEventType = (typeId) => {
    return EVENT_TYPES.find(t => t.id === typeId) || EVENT_TYPES[0];
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div data-testid="schedule-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Schedule</h2>
          <p className="text-neutral-500 text-sm">Manage installations, measurements, and appointments</p>
        </div>
        <Button onClick={() => openCreateModal()} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> Add Event
        </Button>
      </div>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday}>
            Today
          </Button>
          <h3 className="text-lg font-semibold ml-4">
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
        </div>
        
        {/* Event Type Legend */}
        <div className="flex items-center gap-4">
          {EVENT_TYPES.map(type => (
            <div key={type.id} className="flex items-center gap-1">
              <div className={`w-3 h-3 rounded ${type.color}`} />
              <span className="text-xs text-neutral-600">{type.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b bg-neutral-50">
          {DAYS.map(day => (
            <div key={day} className="px-2 py-3 text-center text-sm font-medium text-neutral-600">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {days.map((dayInfo, index) => {
            const dayEvents = getEventsForDay(dayInfo.date);
            const today = isToday(dayInfo.date);
            
            return (
              <div
                key={index}
                className={`min-h-[120px] border-b border-r p-1 ${
                  !dayInfo.currentMonth ? 'bg-neutral-50' : ''
                } ${index % 7 === 6 ? 'border-r-0' : ''}`}
                onClick={() => {
                  setSelectedDay(dayInfo.date);
                  if (dayEvents.length === 0) {
                    openCreateModal(dayInfo.date);
                  }
                }}
              >
                <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                  today ? 'bg-amber-500 text-white' : dayInfo.currentMonth ? 'text-neutral-700' : 'text-neutral-400'
                }`}>
                  {dayInfo.day}
                </div>
                
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map(event => {
                    const eventType = getEventType(event.event_type);
                    return (
                      <div
                        key={event.id}
                        onClick={(e) => { e.stopPropagation(); openEditModal(event); }}
                        className={`text-xs px-1.5 py-0.5 rounded truncate cursor-pointer ${eventType.bgLight} ${eventType.textColor}`}
                        title={event.source === 'sales_order' ? `From SO #${event.so_number}` : event.title}
                      >
                        {formatTime(event.start_time)} {event.source === 'sales_order' && '📋 '}{event.title}
                      </div>
                    );
                  })}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-neutral-500 px-1.5">
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create/Edit Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? 'Edit Event' : 'New Event'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Event Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Installation - Smith Residence"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Event Type</Label>
                <select
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                >
                  {EVENT_TYPES.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Assign To (Installer/Consultant)</Label>
                <select
                  value={formData.installer_id}
                  onChange={(e) => {
                    const assignee = installers.find(i => i.id === e.target.value);
                    setFormData({ 
                      ...formData, 
                      installer_id: e.target.value,
                      installer_name: assignee?.name || ''
                    });
                  }}
                  className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                >
                  <option value="">Select assignee...</option>
                  {/* Group by role */}
                  {installers.filter(i => i.role === 'installer' || !i.role).length > 0 && (
                    <optgroup label="Installers">
                      {installers.filter(i => i.role === 'installer' || !i.role).map(i => (
                        <option key={i.id} value={i.id}>{i.name}</option>
                      ))}
                    </optgroup>
                  )}
                  {installers.filter(i => i.role === 'consultant').length > 0 && (
                    <optgroup label="Consultants">
                      {installers.filter(i => i.role === 'consultant').map(i => (
                        <option key={i.id} value={i.id}>{i.name}</option>
                      ))}
                    </optgroup>
                  )}
                  {/* Fallback if no roles defined - show all */}
                  {installers.every(i => !i.role || i.role === 'installer') && installers.filter(i => i.role === 'consultant').length === 0 && (
                    installers.map(i => (
                      <option key={i.id} value={i.id}>{i.name}</option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time *</Label>
                <Input
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Customer Information Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium text-neutral-700 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" /> Customer Information
              </h4>
              
              <div className="space-y-3">
                <div>
                  <Label>Customer Name</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email
                    </Label>
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      placeholder="customer@email.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Phone
                    </Label>
                    <Input
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-neutral-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Location
                </h4>
                {getGoogleMapsUrl(formData.address, formData.city, formData.state, formData.zip_code) && (
                  <a
                    href={getGoogleMapsUrl(formData.address, formData.city, formData.state, formData.zip_code)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-md hover:bg-blue-100 transition-colors"
                  >
                    <Navigation className="w-3 h-3" /> Get Directions
                  </a>
                )}
              </div>

              <div>
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-3 gap-2 mt-3">
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                />
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="State"
                />
                <Input
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  placeholder="ZIP"
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Email Notification Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium text-neutral-700 mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email Notifications
              </h4>
              
              <div className="space-y-2 text-sm text-neutral-600 bg-neutral-50 p-3 rounded-lg">
                <p className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Admin will receive a notification email
                </p>
                {formData.installer_id && (
                  <p className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    {formData.installer_name || 'Assigned installer'} will receive a notification with dashboard link
                  </p>
                )}
              </div>

              <div className="flex items-center space-x-2 mt-3">
                <Checkbox
                  id="send_customer_email"
                  checked={formData.send_customer_email}
                  onCheckedChange={(checked) => setFormData({ ...formData, send_customer_email: checked })}
                  disabled={!formData.customer_email}
                />
                <label
                  htmlFor="send_customer_email"
                  className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed ${!formData.customer_email ? 'text-neutral-400' : 'text-neutral-700'}`}
                >
                  Send appointment confirmation to customer
                  {!formData.customer_email && <span className="text-xs text-neutral-400 ml-1">(add email first)</span>}
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-4 border-t">
            {editingEvent && (
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  onClick={() => { deleteEvent(editingEvent.id); setShowEventModal(false); }}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => sendReminder(editingEvent.id)}
                  className="text-amber-600 hover:bg-amber-50"
                  title="Send reminder email now"
                >
                  <Bell className="w-4 h-4 mr-2" /> Send Reminder
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setPhotoGalleryEvent(editingEvent);
                    setShowPhotoGallery(true);
                  }}
                  className="text-blue-600 hover:bg-blue-50"
                  title="View job photos"
                  data-testid="view-photos-btn"
                >
                  <Camera className="w-4 h-4 mr-2" /> Photos
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => openMaterialsModal(editingEvent)}
                  className="text-purple-600 hover:bg-purple-50"
                  title="Manage job materials"
                  data-testid="manage-materials-btn"
                >
                  <Package className="w-4 h-4 mr-2" /> Materials
                </Button>
              </div>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setShowEventModal(false)}>Cancel</Button>
              <Button onClick={saveEvent} className="btn-primary">
                {editingEvent ? 'Update' : 'Create'} Event
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Confirmation Dialog */}
      <Dialog open={showUpdateConfirm} onOpenChange={setShowUpdateConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Event Notifications</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-neutral-600">
              Would you like to send update notifications for this event?
            </p>
            
            <div className="space-y-3 bg-neutral-50 p-4 rounded-lg">
              {/* Notify Assignee */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="notify_assignee"
                  checked={updateNotifyOptions.sendToAssignee}
                  onCheckedChange={(checked) => setUpdateNotifyOptions(prev => ({ ...prev, sendToAssignee: checked }))}
                  disabled={!formData.installer_id}
                />
                <div className="flex-1">
                  <label
                    htmlFor="notify_assignee"
                    className={`text-sm font-medium leading-none ${!formData.installer_id ? 'text-neutral-400' : 'text-neutral-700'}`}
                  >
                    Send update to {formData.installer_name || 'Installer/Consultant'}
                  </label>
                  {formData.installer_id && (
                    <p className="text-xs text-neutral-500 mt-1">
                      They will receive an email with updated event details
                    </p>
                  )}
                  {!formData.installer_id && (
                    <p className="text-xs text-neutral-400 mt-1">No assignee selected</p>
                  )}
                </div>
              </div>

              {/* Notify Customer */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="notify_customer"
                  checked={updateNotifyOptions.sendToCustomer}
                  onCheckedChange={(checked) => setUpdateNotifyOptions(prev => ({ ...prev, sendToCustomer: checked }))}
                  disabled={!formData.customer_email}
                />
                <div className="flex-1">
                  <label
                    htmlFor="notify_customer"
                    className={`text-sm font-medium leading-none ${!formData.customer_email ? 'text-neutral-400' : 'text-neutral-700'}`}
                  >
                    Send update to customer
                  </label>
                  {formData.customer_email && (
                    <p className="text-xs text-neutral-500 mt-1">
                      {formData.customer_email}
                    </p>
                  )}
                  {!formData.customer_email && (
                    <p className="text-xs text-neutral-400 mt-1">No customer email provided</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setShowUpdateConfirm(false)}>
              Cancel
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setUpdateNotifyOptions({ sendToAssignee: false, sendToCustomer: false });
                confirmUpdate();
              }}
            >
              Update Without Notifications
            </Button>
            <Button onClick={confirmUpdate} className="btn-primary">
              Update & Send Notifications
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        isOpen={showPhotoGallery}
        onClose={() => {
          setShowPhotoGallery(false);
          setPhotoGalleryEvent(null);
        }}
        eventId={photoGalleryEvent?.id}
        eventTitle={photoGalleryEvent?.title || 'Job'}
        canDelete={true}
      />

      {/* Job Materials Modal */}
      <Dialog open={showMaterialsModal} onOpenChange={setShowMaterialsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Job Materials - {materialsEvent?.title || 'Job'}
            </DialogTitle>
          </DialogHeader>
          
          {loadingMaterials ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Current Materials */}
              <div>
                <h4 className="font-medium mb-2">Assigned Materials</h4>
                {jobMaterials.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded">
                    No materials assigned to this job yet
                  </p>
                ) : (
                  <div className="space-y-2">
                    {jobMaterials.map(material => (
                      <div key={material.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{material.inventory_item_name}</p>
                          <p className="text-sm text-gray-500">
                            Qty: {material.quantity} {material.unit}
                            {material.deducted && (
                              <Badge className="ml-2 bg-green-100 text-green-700">Deducted</Badge>
                            )}
                          </p>
                        </div>
                        {!material.deducted && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMaterial(material.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Material */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Add Material</h4>
                <div className="flex gap-2">
                  <select
                    id="add-material-select"
                    className="flex-1 px-3 py-2 border rounded-md text-sm"
                    defaultValue=""
                    data-testid="add-material-select"
                  >
                    <option value="" disabled>Select inventory item...</option>
                    {inventoryItems.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name} (Stock: {item.stock_level})
                      </option>
                    ))}
                  </select>
                  <Input
                    id="add-material-qty"
                    type="number"
                    min="1"
                    defaultValue="1"
                    className="w-20"
                    data-testid="add-material-qty"
                  />
                  <Button
                    onClick={() => {
                      const select = document.getElementById('add-material-select');
                      const qty = document.getElementById('add-material-qty');
                      if (select.value) {
                        addMaterial(select.value, parseInt(qty.value) || 1);
                        select.value = '';
                        qty.value = '1';
                      }
                    }}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="add-material-btn"
                  >
                    Add
                  </Button>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Materials will be automatically deducted from inventory when job is marked completed.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduleTab;
